import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { promoProducts, promoCategories } from '@/mocks/promos';
import { useCart } from '@/hooks/useCart';
import { useScrollReveal, useStaggerReveal } from '@/hooks/useScrollReveal';

const PromoProductGrid = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const { addItem, hasItem } = useCart();
  const navigate = useNavigate();
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.1 });

  const filtered = activeCategory === 'all'
    ? promoProducts
    : promoProducts.filter((p) => p.category === activeCategory);

  const { containerRef: gridRef, visibleItems } = useStaggerReveal(filtered.length, { threshold: 0.04 });

  const handleCart = (e: React.MouseEvent, p: typeof promoProducts[0]) => {
    e.stopPropagation();
    addItem({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice, image: p.image, color: p.color });
  };

  return (
    <section className="px-6 lg:px-12 pb-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan/60 mb-2">ТОВАРЫ ПО АКЦИИ</p>
            <h2 className="font-orbitron font-black text-3xl md:text-4xl text-white">
              СКИДКИ НА<br />
              <span style={{ color: '#ff2d78', textShadow: '0 0 20px #ff2d7860' }}>ПРЕПАРАТЫ</span>
            </h2>
          </div>

          {/* Category tabs */}
          <div
            className="flex flex-wrap gap-1 p-1 rounded-2xl"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {promoCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
                style={
                  activeCategory === cat.id
                    ? { background: cat.color, color: '#050508', boxShadow: `0 0 12px ${cat.color}60` }
                    : { color: 'rgba(255,255,255,0.4)' }
                }
              >
                <i className={`${cat.icon} text-sm`} />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          {filtered.map((p, i) => (
            <div
              key={p.id}
              onClick={() => navigate(`/product/${p.id}`)}
              className="card-cyber rounded-2xl overflow-hidden group cursor-pointer relative flex flex-col transition-all duration-300"
              style={{
                borderColor: `${p.color}15`,
                opacity: visibleItems[i] ? 1 : 0,
                transform: visibleItems[i] ? 'translateY(0) scale(1)' : 'translateY(28px) scale(0.97)',
                transition: 'opacity 0.55s cubic-bezier(0.22,1,0.36,1), transform 0.55s cubic-bezier(0.22,1,0.36,1)',
              }}
            >
              {/* Discount badge */}
              <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
                <span
                  className="px-2.5 py-0.5 rounded-full font-orbitron font-black text-xs"
                  style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 10px #ff2d7880' }}
                >
                  -{p.discount}%
                </span>
                <span
                  className="px-2 py-0.5 rounded-full font-mono text-[9px] leading-tight"
                  style={{ background: `${p.color}20`, border: `1px solid ${p.color}40`, color: p.color }}
                >
                  {p.promoTag}
                </span>
              </div>

              {/* Image */}
              <div
                className="relative h-44 flex items-center justify-center overflow-hidden"
                style={{ background: `linear-gradient(135deg, #0a0a14, ${p.color}08)` }}
              >
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-10 rounded-full opacity-20 group-hover:opacity-50 transition-opacity"
                  style={{ background: p.color, filter: 'blur(14px)' }}
                />
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-28 h-28 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1"
                  style={{ filter: `drop-shadow(0 6px 14px ${p.color}50)` }}
                />
                <div className="absolute top-2 right-2 w-4 h-4" style={{ borderTop: `1px solid ${p.color}50`, borderRight: `1px solid ${p.color}50` }} />
                <div className="absolute bottom-2 left-2 w-4 h-4" style={{ borderBottom: `1px solid ${p.color}50`, borderLeft: `1px solid ${p.color}50` }} />
              </div>

              {/* Info */}
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-orbitron font-bold text-xs text-white leading-snug mb-1 line-clamp-2">{p.name}</h3>
                <p className="font-mono text-[11px] text-white/35 mb-3 line-clamp-1 flex-1">{p.description}</p>

                {/* Price */}
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <div
                      className="font-orbitron font-black text-xl"
                      style={{ color: p.color, textShadow: `0 0 8px ${p.color}60` }}
                    >
                      {p.price.toLocaleString()}₽
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-white/25 line-through">{p.oldPrice.toLocaleString()}₽</span>
                      <span className="font-mono text-xs" style={{ color: '#39ff14' }}>
                        -{(p.oldPrice - p.price).toLocaleString()}₽
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={(e) => handleCart(e, p)}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
                  style={
                    hasItem(p.id)
                      ? { background: p.color, color: '#050508', boxShadow: `0 0 12px ${p.color}` }
                      : { border: `1px solid ${p.color}`, color: p.color }
                  }
                >
                  <i className={hasItem(p.id) ? 'ri-check-line' : 'ri-shopping-bag-line'} />
                  {hasItem(p.id) ? 'В КОРЗИНЕ' : 'В КОРЗИНУ'}
                </button>

                {/* Rating */}
                <div className="mt-3 pt-2.5 flex items-center gap-1" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className={`text-[9px] ${s <= Math.round(p.rating) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                  ))}
                  <span className="font-mono text-[10px] text-white/25 ml-1">{p.reviews}</span>
                </div>
              </div>

              <div
                className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: `linear-gradient(90deg, transparent, ${p.color}, transparent)` }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PromoProductGrid;
